import {
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  type CommunitySidebarWidgetProps,
  type CommunitySidebarThemingProps,
  SIDEBAR_THEMING_PROP_TO_PARAM,
} from "./types";

/**
 * Ref handle exposed by LegionCommunitySidebar for imperative control.
 */
export interface LegionCommunitySidebarHandle {
  /** Reload the widget iframe */
  refresh: () => void;
}

/**
 * Build the iframe src URL from widget props.
 */
export function buildCommunitySidebarWidgetUrl(
  props: CommunitySidebarWidgetProps
): string {
  const {
    baseUrl,
    title = "Getting Started",
    showGuidelines = true,
    showFaqs = true,
    showTutorials = true,
    showSearchLinks = true,
    communityUrl = "/community",
    guidelinesUrl = "/guidelines",
    faqsUrl = "/faqs",
    theme = "light",
  } = props;

  const url = new URL("/widget/community-sidebar", baseUrl);
  url.searchParams.set("title", title);
  url.searchParams.set("showGuidelines", String(showGuidelines));
  url.searchParams.set("showFaqs", String(showFaqs));
  url.searchParams.set("showTutorials", String(showTutorials));
  url.searchParams.set("showSearchLinks", String(showSearchLinks));
  url.searchParams.set("communityUrl", communityUrl);
  url.searchParams.set("guidelinesUrl", guidelinesUrl);
  url.searchParams.set("faqsUrl", faqsUrl);
  url.searchParams.set("theme", theme);

  // Append theming params
  const themingKeys = Object.keys(SIDEBAR_THEMING_PROP_TO_PARAM) as Array<
    keyof CommunitySidebarThemingProps
  >;
  for (const key of themingKeys) {
    const value = props[key];
    if (value !== undefined && value !== null && String(value).length > 0) {
      url.searchParams.set(SIDEBAR_THEMING_PROP_TO_PARAM[key], String(value));
    }
  }

  return url.toString();
}

/**
 * LegionCommunitySidebar – a React component that embeds the Legion
 * Community Sidebar widget via an iframe.
 */
export const LegionCommunitySidebar = forwardRef<
  LegionCommunitySidebarHandle,
  CommunitySidebarWidgetProps
>(function LegionCommunitySidebar(props, ref) {
  const {
    title = "Getting Started",
    showGuidelines,
    showFaqs,
    showTutorials,
    showSearchLinks,
    communityUrl,
    guidelinesUrl,
    faqsUrl,
    theme = "light",
    height = "auto",
    width = "100%",
    className,
    style,
    onLoad,
    onError,
    baseUrl,
    ...rest
  } = props;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow) {
          iframe.contentWindow.location.reload();
        }
      },
    }),
    []
  );

  const src = useMemo(
    () => buildCommunitySidebarWidgetUrl(props),
    [
      baseUrl, title, showGuidelines, showFaqs, showTutorials, showSearchLinks,
      communityUrl, guidelinesUrl, faqsUrl, theme,
      ...Object.keys(SIDEBAR_THEMING_PROP_TO_PARAM).map(
        (k) => rest[k as keyof typeof rest]
      ),
    ]
  );

  const handleLoad = useCallback(() => {
    onLoad?.({ widget: "community-sidebar", theme });
  }, [theme, onLoad]);

  const handleError = useCallback(() => {
    onError?.("Failed to load community sidebar widget");
  }, [onError]);

  const resolvedHeight = height === "auto" ? "400px" : height;

  return (
    <div
      className={className}
      style={{ width, height: resolvedHeight, ...style }}
      data-testid="legion-community-sidebar"
    >
      <iframe
        ref={iframeRef}
        src={src}
        title="Community Sidebar Widget"
        width="100%"
        height="100%"
        frameBorder="0"
        scrolling="no"
        loading="lazy"
        onLoad={handleLoad}
        onError={handleError}
        style={{ border: "none" }}
      />
    </div>
  );
});

